import { arrayOf, bool, number, oneOfType, shape, string } from 'prop-types'

/**
 * @typedef {Object} DataSharingPartner
 *
 * @property {string}  [id] - ID of the data sharing partner
 * @property {string}  [name] - Name of the data sharing partner
 * @property {string}  [descriptionStringCode] - String code for brief description about the service provided by the data sharing partner
 * @property {string}  [subTitleStringCode] - String code for the type of data shared with the data sharing partner
 * @property {string}  [titleStringCode] - String code for the name of the data sharing provided by the partner
 * @property {string}  [imageUrl] - Relative path in the Atlantis project to the logo of the data sharing application
 * @property {string}  [redirect] - URL of the data sharing partner
 * @property {string}  [featureCategory] - Name of the feature metadata associated to the data sharing application
 * @property {string}  [featureMetaDataId] - ID of the feature metadata
 */
const partnerPropType = shape({
    id: oneOfType([string, number]),
    name: string,
    descriptionStringCode: string,
    subTitleStringCode: string,
    titleStringCode: string,
    imageUrl: string,
    redirect: string,
    featureCategory: string,
    featureMetaDataId: string
})

/**
 * @typedef {Object} DataSharingOption
 *
 * @property {string}  [id] - ID of the data sharing option
 * @property {string}  [name] - Name of the data sharing option
 * @property {string}  [category] - Category of the data sharing option
 * @property {string}  [code] - String code for the data sharing option
 */
export const dataSharingOptionPropType = shape({
    id: oneOfType([string, number]),
    name: string,
    category: string,
    code: string
})

/**
 * @typedef {Object} DataSharingApplication
 *
 * @property {string}  [applicationId] - ID of the data sharing application
 * @property {string}  [applicationName] - Name of the data sharing application
 * @property {boolean}  [dataSharingStopOnCustomerChange] - Flag used to control whether data sharing should be stopped
 * if the unit is moved to a different customer
 * @property {boolean}  [partnerApprovalNeeded] - Flag used in the creation of the data sharing to determine
 * whether an external approval from the partner is required
 * @property {DataSharingPartner[]}  [partners] - Array of data sharing partners offering the data sharing application
 * @property {DataSharingOption[]}  [dataSharingOptions] - Array of data sharing options associated to the data sharing application
 */
export const dataSharingApplicationPropType = shape({
    applicationId: oneOfType([string, number]),
    applicationName: string,
    dataSharingStopOnCustomerChange: bool,
    partnerApprovalNeeded: bool,
    partners: arrayOf(partnerPropType),
    dataSharingOptions: arrayOf(dataSharingOptionPropType)
})
